import { Call, ServiceClient, TokenFunc } from "@securityjourney/service-client";

import { paths, components } from "@securityjourney/svc-experiment/dist/gen/experiment";

export type RunStatus = components["schemas"]["securityjourney.proto.experiment.RunStatus"];

export type LessonContext = {
  tournamentUuid: string;
  tenant: string;
};

export class ExperimentClient extends ServiceClient<paths> {
  constructor(getToken: TokenFunc, baseUrl?: string) {
    super({
      getToken,
      url: baseUrl,
    });
  }

  /**
   * Prepare to run an experiment.
   * This should be called anytime the UI can determine that a user will soon run an experiment.
   * It can be called regularly (e.g., every 5 minutes that a user is on the page) to ensure resources are warm and ready.
   *
   * @param lessonKey
   * @param lessonVersion
   * @param variant
   */
  prepareTest(lessonKey: string, lessonVersion: number, variant: string): Call<void> {
    const func = this.fetcher
      .path("/svc/experiment/reservation/{lessonKey}/v/{lessonVersion}")
      .method("put")
      .create();
    const { init, abort } = this.withAbort();

    const promise = this.withAuthorization(init)
      .then(init =>
        func(
          {
            lessonKey,
            lessonVersion,
            variant,
          },
          init
        )
      )
      .then(() => void 0);
    return {
      promise,
      abort,
    };
  }

  /**
   * Runs tests on the given experiment code.
   *
   * @param lessonKey - a unique string that identifies the lesson_group
   * @param lessonVersion - the version of the lesson
   * @param filePath - the path to the experiment code file
   * @param fileContents - the code to test
   * @param experimentVariant - the variant of the experiment, when a lesson may have more than one experiment
   * @param tournamentContext - optionally identify the tournament, if any, that the user is participating in, and the tenant
   * @returns the status of the experiment run, with a runId that can be used to poll the status
   */
  runTests(
    lessonKey: string,
    lessonVersion: number,
    filePath: string,
    fileContents: string,
    experimentVariant: string,
    tournamentContext?: LessonContext
  ): Call<RunStatus> {
    const func = this.fetcher
      .path("/svc/experiment/run/{lessonKey}/v/{lessonVersion}")
      .method("post")
      .create();
    const { init, abort } = this.withAbort();

    const promise = this.withAuthorization(init)
      .then(init =>
        func(
          {
            lessonKey: lessonKey,
            lessonVersion: lessonVersion,
            variant: experimentVariant,
            context: tournamentContext && {
              tournament: tournamentContext.tournamentUuid,
              tenant: tournamentContext.tenant,
            },
            code: {
              volumes: [
                {
                  name: "experiment",
                  files: [
                    {
                      path: filePath,
                      contents: fileContents,
                    },
                  ],
                },
              ],
            },
          },
          init
        )
      )
      .then(response => response.data);
    return {
      promise,
      abort,
    };
  }

  // svc-experiment can be accessed anonymously with reduced functionality, so do not fail if no token is provided.
  protected withAuthorization(base?: RequestInit): Promise<RequestInit> {
    if (!this.getToken) {
      return Promise.resolve(base || {});
    }
    return super.withAuthorization(base);
  }

  /**
   * Checks the status of the given experiment run.
   *
   * @param runId
   * @return the status of the experiment run, or undefined if the runId is not found
   */
  checkStatus(runId: string): Call<RunStatus | undefined> {
    const func = this.fetcher.path("/svc/experiment/run").method("get").create();
    const { init, abort } = this.withAbort();

    const promise = this.withAuthorization(init)
      .then(init => func({}, init))
      .then(response => (response.data.runs || []).find(run => run.runId === runId));
    return {
      promise,
      abort,
    };
  }
}
